import { Share2, Camera, Calendar } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowShareCalendar } from '../../Redux/store';
import { useState, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { predictTestNumber } from '../../data/huggingface';
import CameraCapture from '../../components/CameraCapture';
import { 
  addUploadInProgress, 
  removeUploadInProgress
} from '../../Redux/userSlice';
import { generateImageUuid } from '../../utils/imageUrls';
import './CalendarBottomMenu.css';

async function uploadOvulationTest(userId, file, dispatch) {
  const uploadId = crypto.randomUUID();
  const startTime = new Date().toISOString();

  dispatch(addUploadInProgress({
    id: uploadId,
    startTime,
    status: 'uploading',
  }));

  try {
    // Upload image to storage using UUID (no extension)
    const imageUUID = generateImageUuid();
    const path = `${userId}/${imageUUID}`;

    const { error: uploadErr } = await supabase.storage
      .from('test-images')
      .upload(path, file, { upsert: false });

    if (uploadErr) {
      throw uploadErr;
    }

    // Create JSONB object with metadata (loading: true during upload)
    const photoUrl = {
      imageUUID,
      source: 'camera',
      userId,
      bucket: 'test-images',
      loading: true
    };

    const { data: insertData, error: dbErr } = await supabase
      .from('ovulation_tests')
      .insert({ user_id: userId, photo_url: { ...photoUrl, loading: false }, value: null, taken_at: startTime })
      .select()
      .single();
    if (dbErr) {
      throw dbErr;
    }

    dispatch(removeUploadInProgress(uploadId));

    predictTestNumber(file)
      .then((value) => {
        if (value !== null) {
          supabase
            .from('ovulation_tests')
            .update({ value })
            .eq('id', insertData.id)
            .then(({ error }) => {
              if (error) {
                /* Error handled */
              }
            });
        }
      })
      .catch((error) => {
      });

    return { photoUrl: { ...photoUrl, loading: false }, testId: insertData.id };
  } catch (error) {
    dispatch(removeUploadInProgress(uploadId));
    throw error;
  }
}

function CalendarBottomMenu({ calendarId, scrollToToday, showTestButton, showShareButton, showTodayButton }) {
  const dispatch = useDispatch();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const userId = useSelector((s) => s.user.authUserId);

  const handleCapture = useCallback((file) => {
    setCameraOpen(false);
    if (!userId) return;
    setUploading(true);
    uploadOvulationTest(userId, file, dispatch)
      .catch((err) => {/* Error handled */})
      .finally(() => setUploading(false));
  }, [userId, dispatch]);

  return (
    <div className="calendar-bottom-menu-container">
      {/* Backdrop blur layer - clipped to match the white shape */}
      <svg className="calendar-white-shape calendar-blur-layer" viewBox="0 0 400 115" preserveAspectRatio="none">
        <path 
          d="M 0,50 
             L 155,50 
             Q 170,50 175,40
             A 32,32 0 0,1 225,40 
             Q 230,50 245,50 
             L 400,50 
             L 400,115 
             L 0,115 
             Z" 
          fill="transparent"
        />
      </svg>

      {/* White SVG shape on top */}
      <svg className="calendar-white-shape" viewBox="0 0 400 115" preserveAspectRatio="none">
        <filter id="calendar-shadow" x="-10%" y="-30%" width="120%" height="160%">
          <feDropShadow dx="0" dy="-4" stdDeviation="4" flood-color="#000000" flood-opacity="0.12" />
        </filter>
        
        <path 
          d="M 0,50 
             L 155,50 
             Q 170,50 175,40
             A 32,32 0 0,1 225,40 
             Q 230,50 245,50 
             L 400,50 
             L 400,115 
             L 0,115 
             Z" 
          fill="#ffffff" 
          fillOpacity="0.8"
          filter="url(#calendar-shadow)"
        />
      </svg>

      {/* Share button — left side */}
      {showShareButton && (
        <button
          onClick={() => dispatch(setShowShareCalendar(true))}
          className="calendar-share-btn"
        >
          <Share2 className="calendar-share-icon" />
          Share
        </button>
      )}

      {/* Add test button — center */}
      {showTestButton && (
        <div>
        <button 
          onClick={() => setCameraOpen(true)}
          disabled={uploading}
          className="calendar-test-btn"
        >
          <span className="calendar-test-icon-wrapper">
            {uploading ? (
              <div className="calendar-test-spinner" />
            ) : (
              <Camera className="calendar-test-icon" />
            )}
          </span> 
          <span className="calendar-test-label">
            {uploading ? 'Processing...' : 'Add test result'}
          </span>
        </button>

        </div>
      )}

      {/* Camera overlay */}
      {cameraOpen && (
        <CameraCapture
          aspectW={6}
          aspectH={1}
          outputWidth={192}
          onCapture={handleCapture}
          onCancel={() => setCameraOpen(false)}
        />
      )}

      {/* Today button — right side */}
      {showTodayButton && (
        <button
          onClick={() => scrollToToday(250, calendarId)}
          className="calendar-today-btn"
        >
          <Calendar className="calendar-today-icon" />
          Today
        </button>
      )}
    </div>
  );
}

export default CalendarBottomMenu;
